import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import {
  EventsService,
  EventServiceProvider,
  createEventService,
} from '@/api/services/events';
import { logError } from '@/utils/logHandling';

interface EventsContextType {
  eventsService: EventsService | null;
  isInitialized: boolean;
}

const EventsContext = createContext<EventsContextType>({
  eventsService: null,
  isInitialized: false,
});

interface EventsProviderProps {
  children: ReactNode;
  provider: EventServiceProvider;
  onInitialized?: () => void;
}

export const EventsProvider: React.FC<EventsProviderProps> = ({
  children,
  provider,
  onInitialized,
}) => {
  const eventsService = useMemo(() => createEventService(provider), [provider]);

  useEffect(() => {
    onInitialized?.();
  }, [eventsService, onInitialized]);

  const contextValue: EventsContextType = useMemo(
    () => ({
      eventsService,
      isInitialized: eventsService !== null,
    }),
    [eventsService],
  );

  return (
    <EventsContext.Provider value={contextValue}>
      {children}
    </EventsContext.Provider>
  );
};

const useEvents = (): EventsContextType => {
  const context = useContext(EventsContext);
  if (context === undefined) {
    logError('useEvents must be used within an EventsProvider');
    throw new Error('useEvents must be used within an EventsProvider');
  }
  return context;
};

export const useEventsService = (): EventsService | null => {
  const { eventsService, isInitialized } = useEvents();

  if (!isInitialized || !eventsService) {
    logError('Events service not yet initialized');
    return null;
  }

  return eventsService;
};
